// src/Components/Drawer/VisHoverTab/VisHoverTab.js

import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';

import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../Firebase/firebase';
import { MenuUpload } from '../../../assets/icons/CustomIconsAdd2';

const ORANGE_COLOR = '#FF9900';
const DARK_BLUE = '#00008B';

// --- HOVER PHOTO UPLOAD ICON SETTINGS ---
const HOVER_UPLOAD_SCALE = 6.9;
const HOVER_UPLOAD_ICON_X = 18;
const HOVER_UPLOAD_ICON_Y = 18;
const HOVER_UPLOAD_TEXT_SIZE = '20px';
const HOVER_UPLOAD_TEXT_Y = 110;

const valueBoxStyle = {
  borderRadius: '4px',
  minHeight: '24px',
  display: 'flex',
  alignItems: 'center',
  padding: '0 4px',
  overflow: 'hidden',
  transition: 'background-color 0.3s ease',
  border: `1px solid ${ORANGE_COLOR}`,
  backgroundColor: '#fff',
};

const labelStyle = {
  color: DARK_BLUE,
  fontWeight: 'bold',
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  marginBottom: '0px',
  marginLeft: '2px',
  lineHeight: 1.2,
};

export default function VisHoverTab({
  tempData,
  isMarkerEditing,
  handleMarkerFieldChange,
  fileInputRef,
  handlePhotoUpload,
  handleClearPhoto,
  stopProp,
}) {
  const [defaultPublicDays, setDefaultPublicDays] = React.useState(0);
  const [makeUppercase, setMakeUppercase] = React.useState(true);

  const hoverTextInputRef = React.useRef(null);
  const cursorPositionRef = React.useRef(null);

  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'MakePublic');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          setDefaultPublicDays(snap.data().makePublicDefault || 0);
        }
      } catch (err) {
        console.error('Error fetching MakePublic settings:', err);
      }
    };
    fetchSettings();

    const unsubFmt = onSnapshot(doc(db, 'settings', 'TextFormatting'), (snap) => {
      if (snap.exists()) {
        const fmtData = snap.data();
        setMakeUppercase(
          fmtData.makeUppercase !== undefined ? !!fmtData.makeUppercase : true,
        );
      } else {
        setMakeUppercase(true);
      }
    });

    return () => {
      unsubFmt();
    };
  }, []);

  React.useEffect(() => {
    if (hoverTextInputRef.current && cursorPositionRef.current !== null) {
      hoverTextInputRef.current.setSelectionRange(
        cursorPositionRef.current,
        cursorPositionRef.current,
      );
      cursorPositionRef.current = null;
    }
  }, [tempData.hoverContent]);

  const getDefaultDate = React.useCallback(() => {
    const d = new Date();
    d.setDate(d.getDate() + defaultPublicDays);
    return d.toISOString().split('T')[0];
  }, [defaultPublicDays]);

  let currentVis = tempData?.visibility;

  if (!currentVis) {
    if (tempData?.publicFromDate && tempData.publicFromDate.trim() !== '') {
      currentVis = `Public From ${tempData.publicFromDate}`;
    } else {
      currentVis = 'Public';
    }
  }

  let radioValue = 'PUBLIC';
  let dateValue = '';

  if (currentVis === 'Private') {
    radioValue = 'PRIVATE';
  } else if (currentVis === 'Restricted') {
    radioValue = 'RESTRICTED';
  } else if (currentVis && currentVis.startsWith('Public From')) {
    radioValue = 'PUBLIC_FROM';
    dateValue = currentVis.replace('Public From', '').trim();
  }

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ mt: 1, ml: 0.5 }}>
        <Typography sx={labelStyle}>VISIBILITY</Typography>

        <Box
          sx={{
            ...valueBoxStyle,
            flexDirection: 'column',
            alignItems: 'stretch',
            padding: '4px 8px',
            backgroundColor: isMarkerEditing ? '#E3F2FD' : '#fff',
          }}>
          <RadioGroup
            value={radioValue}
            onChange={(e) => {
              if (!isMarkerEditing) return;
              const val = e.target.value;

              if (val === 'PUBLIC') handleMarkerFieldChange('visibility', 'Public');
              else if (val === 'PRIVATE')
                handleMarkerFieldChange('visibility', 'Private');
              else if (val === 'RESTRICTED')
                handleMarkerFieldChange('visibility', 'Restricted');
              else if (val === 'PUBLIC_FROM') {
                const d = dateValue || getDefaultDate();
                handleMarkerFieldChange('visibility', `Public From ${d}`);
              }
            }}
            sx={{ width: '100%' }}>
            {/* ROW 1: Public */}
            <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
              <FormControlLabel
                value='PUBLIC'
                control={<Radio size='small' />}
                label={<Typography variant='body2'>Public</Typography>}
                disabled={!isMarkerEditing}
              />
            </Box>

            {/* ROW 2: Public From */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
              }}>
              <FormControlLabel
                value='PUBLIC_FROM'
                control={<Radio size='small' />}
                label={
                  <Typography variant='body2' sx={{ mr: 2 }}>
                    Public From
                  </Typography>
                }
                disabled={!isMarkerEditing}
                sx={{ mr: 0 }}
              />
              {radioValue === 'PUBLIC_FROM' && (
                <TextField
                  type='date'
                  size='small'
                  disabled={!isMarkerEditing}
                  value={dateValue}
                  onChange={(e) =>
                    handleMarkerFieldChange('visibility', `Public From ${e.target.value}`)
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    backgroundColor: '#fff',
                    width: '140px',
                    '& .MuiInputBase-input': {
                      padding: '4px 8px',
                      fontSize: '0.85rem',
                    },
                  }}
                />
              )}
            </Box>

            {/* ROW 3: Restricted & Private */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
              }}>
              <FormControlLabel
                value='RESTRICTED'
                control={<Radio size='small' />}
                label={<Typography variant='body2'>Restricted</Typography>}
                disabled={!isMarkerEditing}
              />
              <FormControlLabel
                value='PRIVATE'
                control={<Radio size='small' />}
                label={<Typography variant='body2'>Private</Typography>}
                disabled={!isMarkerEditing}
              />
            </Box>
          </RadioGroup>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          marginTop: '5px',
        }}>
        <Typography sx={{ ...labelStyle, ml: 0.5 }}>HOVER PHOTO</Typography>

        <Paper
          variant='outlined'
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            borderColor: ORANGE_COLOR,
            borderWidth: '1px',
            borderRadius: 1,
            backgroundColor: 'transparent',
            minHeight: '100px',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              mr: 3,
              borderRight: `1px solid ${ORANGE_COLOR}`,
              pr: 2,
              alignSelf: 'stretch',
              flexShrink: 0,
            }}>
            <IconButton
              disabled={!isMarkerEditing}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              sx={{
                p: 0,
                borderRadius: 2,
                opacity: isMarkerEditing ? 1 : 0.5,
              }}>
              <MenuUpload
                sx={{ fontSize: 60, overflow: 'visible' }}
                iconScale={HOVER_UPLOAD_SCALE}
                textSize={HOVER_UPLOAD_TEXT_SIZE}
                textY={HOVER_UPLOAD_TEXT_Y}
                iconX={HOVER_UPLOAD_ICON_X}
                iconY={HOVER_UPLOAD_ICON_Y}
                label='Upload'
              />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isMarkerEditing
                ? 'rgba(33, 150, 243, 0.1)'
                : 'transparent',
              borderRadius: '8px',
              padding: '0px',
              boxShadow: '2px 2px 5px rgba(0,0,0,0.15)',
              border: `1px solid ${ORANGE_COLOR}`,
              width: '144px',
              height: '144px',
              flexShrink: 0,
              overflow: 'hidden',
              mx: 'auto',
              position: 'relative',
            }}>
            {tempData.hoverImage ? (
              <>
                <img
                  src={tempData.hoverImage}
                  alt='Hover'
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <IconButton
                  size='small'
                  disabled={!isMarkerEditing}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearPhoto();
                  }}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 24,
                    height: 24,
                    minWidth: 24,
                    minHeight: 24,
                    aspectRatio: '1 / 1',
                    borderRadius: '50%',
                    border: '1px solid #000000',
                    padding: 0,
                    boxSizing: 'border-box',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.85)',
                    color: '#000000',
                    zIndex: 20,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,1)',
                    },
                  }}>
                  <CloseIcon sx={{ fontSize: 16, color: '#000000' }} />
                </IconButton>
              </>
            ) : (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ fontSize: '0.75rem' }}>
                No Photo
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      <input
        type='file'
        hidden
        ref={fileInputRef}
        onChange={handlePhotoUpload}
        accept='image/*'
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          mt: 1.5,
          mb: 2,
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1.5 }}>
          <Typography sx={labelStyle}>HOVER TEXT</Typography>
          <Box
            onMouseDown={stopProp}
            style={{ pointerEvents: 'auto' }}
            sx={{
              ...valueBoxStyle,
              backgroundColor: isMarkerEditing ? '#E3F2FD' : '#fff',
              position: 'relative',
              zIndex: 10,
            }}>
            <input
              type='text'
              ref={hoverTextInputRef}
              value={tempData.hoverContent || ''}
              disabled={!isMarkerEditing}
              onClick={stopProp}
              onChange={(e) => {
                const currentCursorPos = e.target.selectionStart;
                let finalValue = e.target.value;
                const nativeEvent = e.nativeEvent;

                if (makeUppercase) {
                  if (nativeEvent && nativeEvent.inputType === 'insertFromPaste') {
                    const selectionStart = e.target.selectionStart;
                    if (nativeEvent.data && typeof selectionStart === 'number') {
                      const dataLen = nativeEvent.data.length;
                      const start = selectionStart - dataLen;
                      if (
                        start >= 0 &&
                        finalValue.substring(start, selectionStart) === nativeEvent.data
                      ) {
                        finalValue =
                          finalValue.slice(0, start) +
                          nativeEvent.data.toUpperCase() +
                          finalValue.slice(selectionStart);
                      } else {
                        finalValue = finalValue.toUpperCase();
                      }
                    } else {
                      finalValue = finalValue.toUpperCase();
                    }
                  } else if (
                    nativeEvent &&
                    nativeEvent.data &&
                    typeof currentCursorPos === 'number' &&
                    nativeEvent.inputType === 'insertText'
                  ) {
                    const dataLen = nativeEvent.data.length;
                    const start = currentCursorPos - dataLen;

                    if (
                      start >= 0 &&
                      finalValue.substring(start, currentCursorPos) === nativeEvent.data
                    ) {
                      const inverted = nativeEvent.data
                        .split('')
                        .map((c) =>
                          c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase(),
                        )
                        .join('');

                      finalValue =
                        finalValue.slice(0, start) +
                        inverted +
                        finalValue.slice(currentCursorPos);
                    } else {
                      finalValue = finalValue.toUpperCase();
                    }
                  } else if (
                    nativeEvent &&
                    nativeEvent.inputType &&
                    nativeEvent.inputType.startsWith('delete')
                  ) {
                    // Let deletion happen naturally
                  } else {
                    finalValue = finalValue.toUpperCase();
                  }
                }

                cursorPositionRef.current = currentCursorPos;
                handleMarkerFieldChange('hoverContent', finalValue);
              }}
              style={{
                border: 'none',
                outline: 'none',
                width: '100%',
                backgroundColor: 'transparent',
                fontSize: '14px',
                lineHeight: '1.2',
                color: DARK_BLUE,
                padding: 0,
                margin: 0,
                cursor: isMarkerEditing ? 'text' : 'default',
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
